import { useEffect, useState } from "react";
import clsx from "clsx";
import {
  fetchCalendarEvents,
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  syncCalendar,
  type CalendarEvent,
  type CalendarEventInput,
} from "../../api";
import type { DealSummary } from "@edge/shared";
import { CalendarEventForm } from "./CalendarEventForm";
import { CalendarListView } from "./CalendarListView";
import { CalendarWeekView } from "./CalendarWeekView";
import { CalendarMonthView } from "./CalendarMonthView";
import {
  addWeeks,
  addMonths,
  weekDaysSun,
  todayPT,
  PT_TZ,
} from "../../lib/calendar-util";

interface CalendarModalProps {
  onClose: () => void;
  deals?: DealSummary[];
}

type View = "list" | "week" | "month";
const VIEW_LS_KEY = "edge_calendar_view";

type ToastTone = "success" | "info" | "error";
interface ToastState {
  message: string;
  tone: ToastTone;
}

/** Pre-fill seed for the "new event" form when triggered by clicking an
 *  empty week-time-slot or month-cell. */
type FormMode =
  | null
  | "new"
  | { kind: "newAt"; startISO: string; allDay: boolean }
  | CalendarEvent;

function loadView(): View {
  try {
    const v = window.localStorage.getItem(VIEW_LS_KEY);
    if (v === "week" || v === "month") return v;
  } catch {
    /* SSR or storage disabled — fall through to list */
  }
  return "list";
}

/**
 * Calendar root modal. Hosts the three view modes, navigation controls,
 * sync button, and the create/edit form. Per-view state (list scroll, week
 * scroll, month grid anchor) lives in the view components; the anchor and
 * view selection live here so switching views preserves where you were.
 */
export function CalendarModal({ onClose, deals }: CalendarModalProps) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FormMode>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [view, setView] = useState<View>(loadView);
  const [anchor, setAnchor] = useState<Date>(() => todayPT());
  const [winWidth, setWinWidth] = useState<number>(() =>
    typeof window === "undefined" ? 1200 : window.innerWidth,
  );

  // Persist view across modal re-opens.
  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_LS_KEY, view);
    } catch {
      /* localStorage disabled — feature degrades to session-scoped */
    }
  }, [view]);

  // Track viewport width to drive the responsive view-tab gating.
  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // <600 px: force LIST and disable WEEK/MONTH tabs.
  const gridsAvailable = winWidth >= 600;
  useEffect(() => {
    if (!gridsAvailable && view !== "list") setView("list");
  }, [gridsAvailable, view]);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const { events } = await fetchCalendarEvents();
      setEvents(events);
    } catch (err) {
      setError(err instanceof Error ? err.message : "load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  // Esc closes modal (only when no nested form is open — let the form handle its own Esc).
  useEffect(() => {
    if (editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, onClose]);

  // Arrow-key nav: ← / → when modal is open, no input has focus, and view is not list.
  useEffect(() => {
    if (editing) return;
    if (view === "list") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement
      ) {
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        nav(-1);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        nav(+1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, view, anchor]);

  useEffect(() => {
    if (!toast) return;
    const dur = toast.tone === "error" ? 3500 : 2600;
    const t = setTimeout(() => setToast(null), dur);
    return () => clearTimeout(t);
  }, [toast]);

  const nav = (delta: number) => {
    if (view === "week") setAnchor(addWeeks(anchor, delta));
    else if (view === "month") setAnchor(addMonths(anchor, delta));
  };
  const goToday = () => setAnchor(todayPT());

  const handleCreate = async (input: CalendarEventInput) => {
    const { event } = await createCalendarEvent(input);
    setEvents((cur) =>
      [...cur, event].sort((a, b) => a.startAt.localeCompare(b.startAt)),
    );
    setEditing(null);
  };
  const handleUpdate = async (id: string, input: Partial<CalendarEventInput>) => {
    const { event } = await updateCalendarEvent(id, input);
    setEvents((cur) => cur.map((e) => (e.id === id ? event : e)));
    setEditing(null);
  };
  const handleDelete = async (id: string) => {
    if (
      !confirm("Delete this event from your calendar? It will also be removed from Outlook.")
    ) {
      return;
    }
    setBusyId(id);
    try {
      await deleteCalendarEvent(id);
      setEvents((cur) => cur.filter((e) => e.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : "delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleManualSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const result = await syncCalendar();
      const { events: fresh } = await fetchCalendarEvents();
      setEvents(fresh);
      const changes = result.inserted + result.updated + result.softDeleted;
      if (changes > 0) {
        setToast({
          message: `Synced · ${changes} change${changes === 1 ? "" : "s"} from Outlook`,
          tone: "success",
        });
      } else {
        setToast({ message: "Already up to date", tone: "info" });
      }
    } catch (err) {
      const detail = err instanceof Error ? err.message : "unknown error";
      setToast({ message: `Sync failed: ${detail.slice(0, 80)}`, tone: "error" });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <div className="write-confirm-backdrop" onClick={onClose}>
        <div
          className="calendar-modal-card"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="calendar-modal-header">
            <div className="calendar-modal-title">EDGE CALENDAR</div>
            <div className="calendar-modal-actions">
              <button
                className="calendar-sync-btn"
                onClick={handleManualSync}
                disabled={syncing}
                title="Force pull recent edits from Outlook"
              >
                {syncing ? "↻ SYNCING…" : "↻ SYNC NOW"}
              </button>
              <button
                className="calendar-new-btn"
                onClick={() => setEditing("new")}
                title="New event"
              >
                + NEW EVENT
              </button>
              <button
                className="calendar-modal-close"
                onClick={onClose}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="calendar-toolbar">
            <div className="calendar-view-switcher">
              <ViewTab
                label="LIST"
                active={view === "list"}
                onClick={() => setView("list")}
              />
              <ViewTab
                label="WEEK"
                active={view === "week"}
                disabled={!gridsAvailable}
                disabledTitle="Switch to a larger screen for grid views"
                onClick={() => setView("week")}
              />
              <ViewTab
                label="MONTH"
                active={view === "month"}
                disabled={!gridsAvailable}
                disabledTitle="Switch to a larger screen for grid views"
                onClick={() => setView("month")}
              />
            </div>
            {view !== "list" && (
              <div className="calendar-nav">
                <button className="calendar-nav-today" onClick={goToday}>
                  TODAY
                </button>
                <button
                  className="calendar-nav-arrow"
                  onClick={() => nav(-1)}
                  aria-label="Previous"
                >
                  ‹
                </button>
                <div className="calendar-nav-label">{viewLabel(view, anchor)}</div>
                <button
                  className="calendar-nav-arrow"
                  onClick={() => nav(+1)}
                  aria-label="Next"
                >
                  ›
                </button>
              </div>
            )}
          </div>

          <div
            className={clsx(
              "calendar-modal-body",
              `view-${view}`,
              !gridsAvailable && "narrow",
            )}
          >
            {loading && <div className="calendar-loading">Loading…</div>}
            {error && (
              <div className="calendar-error">Failed to load: {error}</div>
            )}
            {!loading && !error && view === "list" && (
              <CalendarListView
                events={events}
                busyId={busyId}
                onEdit={(e) => setEditing(e)}
                onDelete={handleDelete}
              />
            )}
            {!loading && !error && view === "week" && (
              <CalendarWeekView
                anchor={anchor}
                events={events}
                onEditEvent={(e) => setEditing(e)}
                onCreateAt={(startISO, allDay) =>
                  setEditing({ kind: "newAt", startISO, allDay })
                }
              />
            )}
            {!loading && !error && view === "month" && (
              <CalendarMonthView
                anchor={anchor}
                events={events}
                onEditEvent={(e) => setEditing(e)}
                onCreateAt={(startISO, allDay) =>
                  setEditing({ kind: "newAt", startISO, allDay })
                }
              />
            )}
          </div>
        </div>
      </div>

      {editing === "new" && (
        <CalendarEventForm
          deals={deals}
          onSave={handleCreate}
          onCancel={() => setEditing(null)}
        />
      )}
      {isNewAt(editing) && (
        <CalendarEventForm
          deals={deals}
          initialStart={editing.startISO}
          initialAllDay={editing.allDay}
          onSave={handleCreate}
          onCancel={() => setEditing(null)}
        />
      )}
      {isExistingEvent(editing) && (
        <CalendarEventForm
          deals={deals}
          existing={editing}
          onSave={(input) => handleUpdate(editing.id, input)}
          onCancel={() => setEditing(null)}
        />
      )}

      {toast && (
        <div className={clsx("edge-toast", `tone-${toast.tone}`)} role="status">
          {toast.message}
        </div>
      )}
    </>
  );
}

function ViewTab({
  label,
  active,
  disabled,
  disabledTitle,
  onClick,
}: {
  label: string;
  active: boolean;
  disabled?: boolean;
  disabledTitle?: string;
  onClick: () => void;
}) {
  return (
    <button
      className={clsx(
        "calendar-view-tab",
        active && "active",
        disabled && "disabled",
      )}
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledTitle : undefined}
    >
      {label}
    </button>
  );
}

function isNewAt(
  v: FormMode,
): v is { kind: "newAt"; startISO: string; allDay: boolean } {
  return !!v && typeof v === "object" && "kind" in v && v.kind === "newAt";
}
function isExistingEvent(v: FormMode): v is CalendarEvent {
  return !!v && typeof v === "object" && !("kind" in v);
}

function viewLabel(view: View, anchor: Date): string {
  if (view === "week") {
    const days = weekDaysSun(anchor);
    const first = days[0];
    const last = days[6];
    const firstStr = first.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: PT_TZ,
    });
    const lastStr = last.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: PT_TZ,
    });
    return `${firstStr} – ${lastStr}`;
  }
  if (view === "month") {
    return anchor.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
      timeZone: PT_TZ,
    });
  }
  return "";
}
